/**
 * useChatInit — session initialization logic
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shouldUseMock } from '../../../api/config';
import { chatApi, type LatestSession } from '../../../features/chat/api';
import {
  getLoopSourceLabel,
  type AeternaLoopContextValue,
  type AeternaLoopSourceType,
  type AeternaLoopStage,
} from '../../../components/aeterna-loop';
import { isSessionReusable } from './types';

export type InitState = 'loading' | 'resumed' | 'new';

const LOOP_SOURCE_TYPES: AeternaLoopSourceType[] = [
  'metric',
  'report',
  'practice',
  'podcast',
  'course',
  'manual',
];

const LOOP_STAGES: AeternaLoopStage[] = ['see', 'understand', 'act', 'ask'];
export const MOCK_CHAT_SESSION_ID = 'mock-session';

function parseSourceType(value: string | null): AeternaLoopSourceType | null {
  if (!value) return null;
  return LOOP_SOURCE_TYPES.includes(value as AeternaLoopSourceType)
    ? (value as AeternaLoopSourceType)
    : null;
}

function parseStage(value: string | null): AeternaLoopStage {
  if (!value) return 'ask';
  return LOOP_STAGES.includes(value as AeternaLoopStage)
    ? (value as AeternaLoopStage)
    : 'ask';
}

function buildReportContext(reportId: string): AeternaLoopContextValue {
  return {
    sourceType: 'report',
    sourceId: reportId,
    sourceTitle: '体检报告',
    stage: 'ask',
    evidenceBoundary: '基于当前体检报告和已授权健康资料回答；不能替代医生诊断。',
  };
}

export function useChatInit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const useMock = shouldUseMock('chat');
  const explicitSessionId = searchParams.get('sessionId');
  const fromReport = searchParams.get('fromReport') === '1';
  const reportIdParam = searchParams.get('reportId');
  const routeContext = useMemo<AeternaLoopContextValue | null>(() => {
    if (fromReport && reportIdParam) return buildReportContext(reportIdParam);
    const sourceType = parseSourceType(searchParams.get('contextType'));
    const sourceTitle = searchParams.get('contextTitle');
    if (!sourceType || !sourceTitle) return null;
    return {
      sourceType,
      sourceTitle,
      sourceId: searchParams.get('sourceId') ?? undefined,
      sourceDate: searchParams.get('sourceDate') ?? undefined,
      stage: parseStage(searchParams.get('contextStage')),
      evidenceBoundary:
        searchParams.get('contextBoundary') ??
        '基于当前页面带入的上下文回答；不能替代医生诊断。',
      askPrompt: searchParams.get('contextPrompt') ?? undefined,
    };
  }, [fromReport, reportIdParam, searchParams]);

  const [sessionId, setSessionId] = useState<string | null>(explicitSessionId);
  const [initState, setInitState] = useState<InitState>(
    explicitSessionId ? 'resumed' : 'loading',
  );
  const [latestSession, setLatestSession] = useState<LatestSession | null>(null);
  const [loopContext, setLoopContext] = useState<AeternaLoopContextValue | null>(routeContext);
  const contextProcessedRef = useRef(false);

  useEffect(() => {
    if (explicitSessionId) {
      setSessionId(explicitSessionId);
      setInitState('resumed');
      return;
    }

    if (useMock) {
      if (!contextProcessedRef.current) {
        contextProcessedRef.current = true;
        setLoopContext(routeContext);
      }
      setLatestSession(null);
      setSessionId(MOCK_CHAT_SESSION_ID);
      setInitState('new');
      if (routeContext) navigate('/ask', { replace: true });
      return;
    }

    if (contextProcessedRef.current) return;

    let isCancelled = false;
    const init = async () => {
      try {
        if (routeContext) {
          contextProcessedRef.current = true;
          setLoopContext(routeContext);
          const metadata = {
            loopContext: routeContext,
          };
          if (routeContext.sourceType === 'report' && routeContext.sourceId) {
            Object.assign(metadata, {
              reportContext: {
                reportId: routeContext.sourceId,
                source: 'report_detail_page',
              },
            });
          }
          const title = `${getLoopSourceLabel(routeContext.sourceType)}咨询`;
          const newSession = await chatApi.createSession(title, metadata);
          if (isCancelled) return;
          setLatestSession(null);
          setSessionId(newSession.id);
          setInitState('new');
          navigate('/ask', { replace: true });
          return;
        }

        const { session } = await chatApi.getLatestSession();
        if (isCancelled) return;
        setLoopContext(null);

        if (session && isSessionReusable(session)) {
          setLatestSession(session);
          setSessionId(session.id);
          setInitState('resumed');
        } else {
          const newSession = await chatApi.createSession();
          if (isCancelled) return;
          setLatestSession(null);
          setSessionId(newSession.id);
          setInitState('new');
        }
      } catch {
        if (!isCancelled) {
          try {
            const fallbackSession = await chatApi.createSession();
            if (isCancelled) return;
            setLatestSession(null);
            setSessionId(fallbackSession.id);
          } catch { /* ensureSession handles this */ }
          setInitState('new');
        }
      }
    };

    init();
    return () => { isCancelled = true; };
  }, [explicitSessionId, routeContext, navigate, useMock]);

  return { sessionId, setSessionId, initState, setInitState, latestSession, setLatestSession, fromReport, loopContext };
}
